import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { map, tap } from 'rxjs/operators'

import { PlatformSettings } from '@shared/models/platform-settings.model'
import { FALLBACK_ADMIN_PLATFORM_SETTINGS } from 'src/environments/go-api-fallbacks'
import { environment } from 'src/environments/environment'
import { domainName } from '@core/helpers/domain-name'
import { ApiService } from '@shared/services/api.service'
import { User } from '@shared/models/user.model'
import { AuthService } from '@app/modules/auth/auth.service'
import { AdminTeamService } from '@app/modules/admin/admin-team/admin-team.service'
import { ClientsService } from '@app/modules/admin/clients/clients.service'
import { ResellerService } from '@app/modules/admin/reseller/services/reseller.service'
import { Currency } from '@shared/models/currency.model'
@Injectable({ providedIn: 'root' })
export class PlatformSettingsService {
  private readonly platformSettings = new BehaviorSubject<PlatformSettings>(null)
  readonly platformSettings$ = this.platformSettings.asObservable()

  private clientCurrency: Currency

  get currentUser(): User {
    return this.authService.getCurrentUser()
  }

  constructor(
    private readonly apiService: ApiService,
    public readonly authService: AuthService,
    private readonly adminTeamService: AdminTeamService,
    private readonly clientsService: ClientsService,
    private readonly resellerService: ResellerService,
  ) {}

  loadPlatformSettings(): Observable<PlatformSettings> {
    const settings$ = this.currentUser ? this.getPlatformSettingsUser() : this.getPlatformSettingsByDomain()

    return settings$.pipe(
      tap((settings) => {
        const settingsInstance = new PlatformSettings(settings)
        settingsInstance.setImages(this.apiService.fileUrl)
        this.setPlatformSettings(settingsInstance)
        this.setClientCurrency(settings.currency)
      }),
    )
  }

  setPlatformSettings(settings: PlatformSettings): void {
    this.platformSettings.next(settings)
  }

  hasIntercomAsync(): Observable<boolean> {
    const toBoolean = (settings: PlatformSettings | null) => !!settings && !!settings.linkKnowledgeBase

    return this.platformSettings$.pipe(map(toBoolean))
  }

  private getPlatformSettingsUser(): Observable<PlatformSettings> {
    switch (true) {
      case this.currentUser.isAdmin():
        return this.adminTeamService.getPlatformSettings()
      case this.currentUser.isClient():
        return this.clientsService.getPlatformSettings()
      case this.currentUser.isReseller(): {
        const id = this.currentUser.team.resellerId
        return this.resellerService.getPlatformSettings(id)
      }
      default:
        return of(new PlatformSettings())
    }
  }

  private getPlatformSettingsByDomain(): Observable<PlatformSettings> {
    if (environment.fakeApis) {
      return of(FALLBACK_ADMIN_PLATFORM_SETTINGS as unknown as PlatformSettings)
    }
    const isProduction = process.env.NODE_ENV !== 'development'
    const domain = isProduction ? domainName() : domainName(environment.url)
    return <Observable<PlatformSettings>>(
      this.apiService.get('/platform-settings/domain', { params: { domain } }).pipe(map((res) => res['data']))
    )
  }

  getClientCurrency(): Currency {
    return (
      this.clientCurrency || {
        id: 1,
        name: 'Australian dollar',
        code: 'AUD',
        symbol: '$',
        decimals: 2,
      }
    )
  }

  setClientCurrency(currency: Currency): void {
    this.clientCurrency = currency
  }
}
